/**
 * Color picker with palette (react-colorful).
 * Professional saturation/hue picker with draggable circle + hex input.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { HexColorPicker, HexColorInput } from 'react-colorful';
import { cn } from '@/lib/utils';

interface ColorPickerProps {
  color: string;
  onChange: (color: string) => void;
  /** Optional placeholder when empty */
  placeholder?: string;
  /** Optional preset hex colors shown as quick-pick swatches */
  presets?: readonly string[];
  className?: string;
  /** id on the trigger button (e.g. <label htmlFor>) */
  triggerId?: string;
  /** id / name on the hex text field inside the popover */
  hexInputId?: string;
  hexInputName?: string;
}

const DEFAULT_PRESETS = [
  '#ef4444',
  '#f97316',
  '#eab308',
  '#22c55e',
  '#14b8a6',
  '#3b82f6',
  '#8b5cf6',
  '#ec4899',
  '#6b7280',
] as const;

export const ColorPicker: FC<ColorPickerProps> = ({
  color,
  onChange,
  placeholder = '#000000',
  presets = DEFAULT_PRESETS,
  className,
  triggerId,
  hexInputId,
  hexInputName,
}) => {
  const displayColor = color || '#6b7280';
  const isValidHex = /^#[0-9a-fA-F]{6}$/.test(color);

  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          type="button"
          id={triggerId}
          className={cn(
            'flex h-10 w-full items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-left text-sm transition-colors',
            'hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            className,
          )}
          aria-label="Pick a color"
        >
          <span
            className="h-6 w-6 shrink-0 rounded border border-border"
            style={{ backgroundColor: isValidHex ? color : displayColor }}
          />
          <span className="min-w-0 flex-1 font-mono text-sm text-foreground">
            {color || placeholder}
          </span>
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          className="z-[300] w-auto rounded-lg border border-border bg-popover p-3 shadow-lg outline-none"
        >
          <HexColorPicker
            color={color || '#6b7280'}
            onChange={onChange}
            style={{ width: '100%', height: 160, minWidth: 200 }}
          />
          <div className="mt-3 flex items-center gap-2">
            {hexInputId ? (
              <label
                htmlFor={hexInputId}
                className="shrink-0 text-xs text-muted-foreground"
              >
                Hex
              </label>
            ) : (
              <span className="shrink-0 text-xs text-muted-foreground">Hex</span>
            )}
            <HexColorInput
              id={hexInputId}
              name={hexInputName}
              color={color || '#000000'}
              onChange={onChange}
              prefixed
              className="min-w-0 flex-1 rounded border border-input bg-background px-2 py-1.5 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
            {color && (
              <button
                type="button"
                onClick={() => onChange('')}
                className="shrink-0 rounded px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                Clear
              </button>
            )}
          </div>
          {presets.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5 border-t border-border pt-3">
              {presets.map((hex) => (
                <button
                  key={hex}
                  type="button"
                  onClick={() => onChange(hex)}
                  className={cn(
                    'h-6 w-6 rounded border-2 transition-transform hover:scale-110',
                    color === hex ? 'border-foreground ring-2 ring-primary' : 'border-transparent',
                  )}
                  style={{ backgroundColor: hex }}
                  aria-label={`Select ${hex}`}
                />
              ))}
            </div>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default ColorPicker;
